import { useEffect, useMemo, useState } from 'react';
import './css/Acp.css';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import { Link, useNavigate } from 'react-router-dom';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import PaginationBar from './components/PaginationBar';
import ResponsiveShadow from './components/ResponsiveShadow';
import DefaultModal from './components/modals/DefaultModal';
import usePagination10 from './components/hooks/usePagination10';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

function Pessoas() {
  // Adiciona controle da página atual
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [linhaSelecionada, setLinhaSelecionada] = useState(null);
  const [pessoasOriginais, setPessoasOriginais] = useState([]);
  const [atualizar, setAtualizar] = useState(0);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [modalData, setModalData] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [filtroCargo, setFiltroCargo] = useState('');

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending',
  });

  const showDefaultModal = (type, message) => {
    setModalData({ show: true, type: type, message: message });
  };
  const closeDefaultModal = () => {
    setModalData(prevModalData => ({ ...prevModalData, show: false }));
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    setLoading(true);
    axios
      .get(`${API_BASE_URL.PESSOA}/all`, {
        params: { cnpj },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setPessoasOriginais(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter registros:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar, token, cnpj, navigate]);

  const pessoasOrdenadas = useMemo(() => {
    let lista = [...pessoasOriginais];

    // Filtro por cargo
    if (filtroCargo === 'diretoria') {
      lista = lista.filter(p => p.cargoDiretoria);
    } else if (filtroCargo === 'deliberativo') {
      lista = lista.filter(p => p.cargoDeliberativo);
    } else if (filtroCargo === 'fiscal') {
      lista = lista.filter(p => p.cargoFiscal);
    }

    // Ordenação
    if (sortConfig.key !== null) {
      lista.sort((a, b) => {
        const valA = (a[sortConfig.key] || '').toString().toLowerCase();
        const valB = (b[sortConfig.key] || '').toString().toLowerCase();
        if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }

    return lista;
  }, [pessoasOriginais, sortConfig, filtroCargo]);

  const requestSort = key => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = columnKey => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === 'ascending' ? (
        <i className="bi bi-arrow-up-short ms-1"></i>
      ) : (
        <i className="bi bi-arrow-down-short ms-1"></i>
      );
    }
    return (
      <i className="bi bi-arrow-down-up ms-1" style={{ opacity: 0.3 }}></i>
    );
  };

  const totalItems = pessoasOrdenadas.length;
  const { setPagination } = usePagination10(totalItems);

  // Sempre que o filtro mudar, volta para a primeira página
  useEffect(() => {
    setPagination.onFirstItems(); // Vai para a primeira página
  }, [filtroCargo]);

  // Force update da paginação quando totalItems mudar
  useEffect(() => {
    setPagination.onFirstItems(); // Recalcula a paginação
  }, [totalItems]);

  const selectedPessoas = pessoasOrdenadas.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  function handleDelete(c) {
    axios
      .delete(`${API_BASE_URL.PESSOA}/delete`, {
        params: {
          cnpjEscola: c.cnpjEscola,
          cpf: c.cpf,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        setAtualizar(prev => prev + 1);
        showDefaultModal('success', 'O registro foi excluído com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir registro:', error);
        showDefaultModal('error', 'Erro ao excluir o registro.');
      });
  }

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="Pessoas Cadastradas" icon="bi-people" />
        <PageContent>
          <Toolbar>
              <Link
                to="/pessoa"
                className="btn btn-success btn-sm botao-igual"
              >
                <i className="bi bi-plus-lg me-1" />Novo
              </Link>
              <Link
                to="/pessoa"
                className={`btn btn-primary btn-sm botao-igual${!linhaSelecionada ? ' disabled' : ''}`}
                tabIndex={!linhaSelecionada ? -1 : 0}
                aria-disabled={!linhaSelecionada}
                state={linhaSelecionada ? { pessoa: linhaSelecionada } : undefined}
                onClick={e => { if (!linhaSelecionada) e.preventDefault(); }}
              >
                <i className="bi bi-pencil me-1" />Editar
              </Link>
              <button
                className="btn btn-danger btn-sm botao-igual centralizar-se-impar"
                disabled={!linhaSelecionada}
                onClick={() => linhaSelecionada && handleDelete(linhaSelecionada)}
              >
                <i className="bi bi-trash me-1" />Excluir
              </button>
              <div className="input-group input-group-sm toolbar-search">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <select
                  id="filtroCargo"
                  className="form-select form-select-sm"
                  value={filtroCargo}
                  onChange={e => setFiltroCargo(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="diretoria">Diretoria</option>
                  <option value="deliberativo">Conselho Deliberativo</option>
                  <option value="fiscal">Conselho Fiscal</option>
                </select>
              </div>
          </Toolbar>

          <TableContainer isLoading={false}>
            <DataTable head={
              <tr>
                <th className="px-2 align-middle" style={{ width: '3rem' }}>#</th>
                <th className="px-2 align-middle" style={{ width: '5%' }}>CPF</th>
                <th className="px-2 align-middle">Nome</th>
                <th className="px-2 align-middle">Diretoria</th>
                <th className="px-2 align-middle">Conselho Deliberativo</th>
                <th className="px-2 align-middle">Conselho Fiscal</th>
              </tr>
            }>
              <tbody>
                {selectedPessoas.map((c, idx) => (
                  <tr
                    key={`${c.cnpjEscola}-${c.cpf}`}
                    className={
                      linhaSelecionada &&
                      linhaSelecionada.cpf === c.cpf &&
                      linhaSelecionada.cnpjEscola === c.cnpjEscola
                        ? 'table-primary'
                        : ''
                    }
                    style={{ cursor: 'pointer' }}
                    onClick={() => setLinhaSelecionada(c)}
                  >
                    <td className="px-2">{setPagination.startIndex + idx + 1}</td>
                    <td className="px-2">{c.cpf}</td>
                    <td className="px-2">{c.nome}</td>
                    <td className="px-2">{c.cargoDiretoria}</td>
                    <td className="px-2">{c.cargoDeliberativo}</td>
                    <td className="px-2">{c.cargoFiscal}</td>
                  </tr>
                ))}
                {pessoasOrdenadas.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center">Nenhuma pessoa encontrada.</td>
                  </tr>
                )}
              </tbody>
            </DataTable>
          </TableContainer>

        </PageContent>

        {pessoasOrdenadas.length > 0 && (
          <PaginationBar setPagination={setPagination} />
        )}
      </ResponsiveShadow>

      <DefaultModal
        show={modalData.show}
        type={modalData.type}
        onClose={closeDefaultModal}
        message={modalData.message}
      />
    </Layout>
  );
}

export default Pessoas;
